// The one line at the top when the API is slow or unreachable (index.css).
//
// It used to check once, when the app opened, and say only "responding
// slowly". The server's speed swings within a minute (a 2026-10-18 reading:
// 0.15 s, then 3 s seconds later, as background jobs woke on a small box),
// so a person who landed during a burst saw the line for the whole visit,
// with no idea how slow. Now it says how long the server took, and checks
// again every minute while the tab is open, so it goes away once the server
// answers in time (2026-10-18).
import React, { useEffect, useState } from 'react';

/** Slower than this is "slow". */
export const SLOW_MS = 3000;
/** No answer within this is "not answering". */
export const HEALTH_DEADLINE_MS = 6000;
/** How often the check runs again while the tab is open. */
export const RECHECK_MS = 60_000;

/** What one health check says; null when the server answered in time.
 *  `ok` is the response's ok, `ms` how long it took, `failed` no answer. */
export function healthVerdict({ ok = false, ms = null, failed = false } = {}) {
  if (failed) return { kind: 'down', ms: null };
  if (!ok) return { kind: 'error', ms };
  if (ms > SLOW_MS) return { kind: 'slow', ms };
  return null;
}

const seconds = (ms) => `${(Math.max(0, ms) / 1000).toFixed(1)} s`;

/** The words for a verdict. */
export function serverSlowText(verdict) {
  if (!verdict) return '';
  if (verdict.kind === 'down') {
    return `The server did not answer within ${HEALTH_DEADLINE_MS / 1000} seconds. Pages may not load right now; nothing is lost.`;
  }
  if (verdict.kind === 'error') {
    return `The server answered with an error${verdict.ms != null ? ` after ${seconds(verdict.ms)}` : ''}. Pages may not load right now; nothing is lost.`;
  }
  return `The server took ${seconds(verdict.ms)} to answer just now. Pages may load slowly; nothing is lost.`;
}

/**
 * Checks the server now, again every `everyMs` while the tab is visible,
 * and whenever the tab comes back into view. `fetcher(deadlineMs)` returns
 * the health response (or throws on no answer); `now` is the clock.
 * Returns the latest verdict (null when the server answered in time).
 */
export function useServerHealth(dep, { fetcher, now = () => Date.now(), everyMs = RECHECK_MS } = {}) {
  const [verdict, setVerdict] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let running = false;
    const check = async () => {
      if (running || (typeof document !== 'undefined' && document.visibilityState === 'hidden')) return;
      running = true;
      const started = now();
      try {
        const response = await fetcher(HEALTH_DEADLINE_MS);
        if (!cancelled) setVerdict(healthVerdict({ ok: Boolean(response?.ok), ms: now() - started }));
      } catch (e) {
        console.warn('[App] backend health check failed:', e?.message || e);
        if (!cancelled) setVerdict(healthVerdict({ failed: true }));
      } finally {
        running = false;
      }
    };
    check();
    const timer = setInterval(check, everyMs);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dep]);
  return verdict;
}

// On a phone it is drawn inside the content column, under the fixed bar, or
// at the foot of a page that draws its own strip: at the top of the document
// it sat beneath the bar, where nobody could read it (2026-09-30).
export const ServerSlow = ({ verdict, atFoot = false }) => (
  <div className={`server-slow${atFoot ? ' is-at-foot' : ''}`} role="status" aria-live="polite">
    {serverSlowText(verdict)}
  </div>
);

export default ServerSlow;
